import { ListItem } from '@/components';
import APP_CONFIG from '@/config/app';
import { formatNumberToAmountInUsdString } from '@/utils/formatUtils';
import { format, startOfDay, startOfMonth, startOfWeek } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
const { MAIN_BACKDROP_BLUR } = APP_CONFIG.STYLES;
interface Props {
  data: { value: number; time: string; label: string }[];
  startAtZero?: boolean;
  className?: string;
  yAxisType?: 'usd' | 'token' | 'percentage';
  tokenTicker?: string;
  xAxisLabels?: string[];
  yAxisLabels?: string[];
  chartHeight?: number;
  chartMinHeight?: number;
  chartMaxHeight?: number;
  chartId: string;
  chartColor?: string;
  noDataNode?: React.ReactNode;
  timeAggregation?: 'daily' | 'weekly' | 'monthly';
}
const StyledAreaChart: React.FC<Props> = ({
  data = [],
  startAtZero = false,
  className = '',
  yAxisType = 'usd',
  tokenTicker,
  xAxisLabels,
  yAxisLabels,
  chartHeight = 280,
  chartMinHeight = 160,
  chartMaxHeight = 320,
  chartId,
  chartColor = '#C4FAC3',
  noDataNode,
  timeAggregation = 'weekly',
}) => {
  const parseData = useCallback(
    (data: { value: number; time: string; label: string }[]) => {
      const sortedData = [...data].sort(
        (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
      );
      const aggregatedData = new Map();
      sortedData.forEach((item) => {
        const date = new Date(item.time);
        let key: string;
        let compareDate: Date;
        switch (timeAggregation) {
          case 'daily':
            key = format(startOfDay(date), 'yyyy-MM-dd');
            compareDate = startOfDay(date);
            break;
          case 'weekly':
            key = format(startOfWeek(date), 'yyyy-MM-dd');
            compareDate = startOfWeek(date);
            break;
          case 'monthly':
            key = format(startOfMonth(date), 'yyyy-MM');
            compareDate = startOfMonth(date);
            break;
        }
        if (
          !aggregatedData.has(key) ||
          date.getTime() > new Date(aggregatedData.get(key).time).getTime()
        ) {
          aggregatedData.set(key, { ...item, compareDate });
        }
      });
      return Array.from(aggregatedData.values()).map((item) => ({
        ...item,
        time: format(
          new Date(item.time),
          timeAggregation === 'monthly' ? 'MMM yyyy' : 'dd MMM, yy',
        ),
        key: chartId,
        value: Number(item.value) || 0,
      }));
    },
    [chartId, timeAggregation],
  );
  const [parsedData, setParsedData] = useState(() => parseData(data));
  const prevDataRef = useRef(data);
  useEffect(() => {
    if (JSON.stringify(data) !== JSON.stringify(prevDataRef.current)) {
      setParsedData(parseData(data));
      prevDataRef.current = data;
    }
  }, [data, parseData]);
  const formatYAxisValue = useCallback(
    (value: number): string => {
      switch (yAxisType) {
        case 'usd':
          return formatNumberToAmountInUsdString(value, true);
        case 'token':
          return `${value.toFixed(2)}`;
        case 'percentage':
          return `${value.toFixed(2)}%`;
        default:
          return value.toString();
      }
    },
    [yAxisType],
  );
  const customYAxisTick = useCallback(
    (props: any) => {
      const { x, y, payload } = props;
      const formattedValue = yAxisLabels
        ? yAxisLabels[payload.index]
        : formatYAxisValue(payload.value);
      const tokenSuffix = yAxisType === 'token' ? tokenTicker : '';
      return (
        <g
          transform={`translate(${x},${y})`}
          key={`${chartId}-tick-${payload.value}`}
        >
          <text x={0} y={0} textAnchor="end" fill="#666">
            {formattedValue}
            {tokenSuffix && (
              <tspan style={{ fontSize: '0.5em' }}>{tokenSuffix}</tspan>
            )}
          </text>
        </g>
      );
    },
    [formatYAxisValue, yAxisLabels, yAxisType, tokenTicker, chartId],
  );
  const CustomTooltip = useCallback(
    ({ active, payload, label }: any) => {
      if (active && payload && payload.length) {
        const formattedValue = yAxisLabels
          ? yAxisLabels[payload[0].index]
          : formatYAxisValue(payload[0].value);
        return (
          <div
            className={`rounded-2xs py-1 px-2 flex items-center justify-center flex-gap bg-primary bg-opacity-10 backdrop-blur${MAIN_BACKDROP_BLUR} border border-solid border-primary/10`}
          >
            <ListItem mainText={formattedValue} subText={label} />
          </div>
        );
      }
      return null;
    },
    [formatYAxisValue, yAxisLabels],
  );
  const minValue = Math.min(...parsedData.map((item) => item.value));
  const maxValue = Math.max(...parsedData.map((item) => item.value));
  const adjustedMin = startAtZero ? 0 : minValue;
  const adjustedMax = maxValue;
  const numberOfTicks = 6;
  const tickInterval = (adjustedMax - adjustedMin) / (numberOfTicks - 1);
  const yTicks = Array.from(
    { length: numberOfTicks },
    (_, i) => adjustedMin + i * tickInterval,
  );
  const yDomain = [yTicks[0], yTicks[yTicks.length - 1]];
  const xMinValue = 0;
  const xMaxValue = parsedData.length - 1;
  const xTickInterval = (xMaxValue - xMinValue) / (numberOfTicks - 1);
  const xTicks = Array.from({ length: numberOfTicks }, (_, i) =>
    Math.round(xMinValue + i * xTickInterval),
  );
  return (
    <div
      className={`flex flex-col w-full mt-6 items-center justify-center text-xsm ${className}`}
      style={{ height: chartHeight }}
    >
      {parsedData.length === 0 && noDataNode ? (
        <div
          className="flex flex-col w-full -mt-8"
          style={{ height: chartHeight }}
        >
          {noDataNode}
        </div>
      ) : (
        <ResponsiveContainer
          width="100%"
          height={chartHeight}
          minHeight={chartMinHeight}
          maxHeight={chartMaxHeight}
        >
          <AreaChart
            data={parsedData}
            margin={{
              top: 12,
              right: 12,
              left: 0,
              bottom: 0,
            }}
            key={`${chartId}-${JSON.stringify(prevDataRef.current)}`}
          >
            <defs>
              <linearGradient
                id={`${chartId}-colorPv`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="-7.89%"
                  stopColor={chartColor}
                  stopOpacity={0.1}
                />
                <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              type="category"
              tickLine={false}
              axisLine={false}
              ticks={
                xAxisLabels
                  ? xTicks.map((index) => xAxisLabels[index])
                  : xTicks.map((index) => parsedData[index]?.time)
              }
              tickMargin={12}
              allowDuplicatedCategory={false}
              tick={{
                textAnchor: 'end',
              }}
            />
            <YAxis
              type="number"
              tick={customYAxisTick}
              tickLine={false}
              axisLine={false}
              domain={yDomain}
              ticks={yTicks}
              interval={0}
              tickCount={numberOfTicks}
              tickMargin={12}
              allowDataOverflow={true}
            />
            <CartesianGrid
              vertical={false}
              horizontal={false}
              strokeDasharray="6 6"
              strokeWidth={0.5}
              stroke="#FFFFFF"
              strokeOpacity={0.1}
            />
            <Tooltip
              content={CustomTooltip}
              cursor={{ stroke: chartColor, strokeWidth: 0.2 }}
              wrapperStyle={{ outline: 'none' }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              fill={`url(#${chartId}-colorPv)`}
              fillOpacity={1}
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={1500}
              animationEasing="ease-in-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
export default StyledAreaChart;
